import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PublishLayout } from '../components/PublishLayout';
import { Upload, MoreVertical, Image as ImageIcon, FileText, Trash2, RefreshCw, Link2, Play, Pencil, Plus, ArrowLeft, Share2, Video, X, Subtitles as SubtitlesIcon, ChevronRight, Users, ArrowLeftRight } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { EpisodePreviewDialog, PreviewableEpisode } from '../components/EpisodePreviewDialog';
import { ThumbnailUploader } from '../components/ThumbnailUploader';
import { ShareModal } from '../components/ShareModal';
import BonusContentScreen from '../components/BonusContentScreen';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Info } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from 'sonner';
import { usePublish } from '../contexts/PublishContext';
import { useAuth } from '../contexts/AuthContext';
import { useSlackNotification } from '../hooks/useSlackNotification';
import { ShowMetaDataType } from '../types/Show';
import { DropArea } from '../components/DropArea';
import { DropZone } from '../components/DropZone';
import { LoadingScreen } from '../components/LoadingScreen';
import { fetchShow, fetchShowMetadata, updateShowMetadata, deleteShowMetadata, updateThumbnailVariant, transformVodUrlToHttp } from '../services/showService';
import { Show } from '../types/Show';
import { ThumbnailVariantsSection } from '../components/ThumbnailVariantsSection';
import { EditVariantDialog } from '../components/EditVariantDialog';
import { fileUploadService } from '../services/fileUploadService';
import { reelUploadService, EpisodeUploadStatus, EpisodePreview } from '../services/reelUploadService';

// Mock translation function
const t = (key: string) => key;

// Thumbnail dimension constants
const REELS_THUMBNAIL_4X5_WIDTH = 1000;
const REELS_THUMBNAIL_4X5_HEIGHT = 1250;
const REELS_THUMBNAIL_5X8_WIDTH = 1000;
const REELS_THUMBNAIL_5X8_HEIGHT = 1600;
const REELS_THUMBNAIL_2X1_2WIDTH = 1200;
const REELS_THUMBNAIL_2X1_2HEIGHT = 600;
export const THUMBNAIL_240X70_HEIGHT = 70;
export const THUMBNAIL_240X70_WIDTH = 240;
export const THUMBNAIL_24X7_ASPECT_RATIO = 24 / 7;
const DEFAULT_IMAGE_EXPORT_FORMAT = 'jpeg';

// Sequence parsing function (same as ReelUploadSection.tsx)
const parseSequence = (n: string): number => {
  const lower = n.toLowerCase();
  if (lower.includes('trailer')) return 0;

  // Check for specific episode patterns first (case-insensitive)
  // Supports: episode_1, Episode_1, episode1, Episode1, etc.
  const episodeMatch = n.match(/episode[_\s]*(\d+)/i);
  if (episodeMatch) {
    const num = parseInt(episodeMatch[1], 10);
    return !isNaN(num) && num >= 0 ? num : 1;
  }

  // Extract all numbers from filename for fallback
  const numbers = n.match(/\d+/g);
  if (!numbers || numbers.length === 0) return 1;

  // Convert to integers and filter out invalid ones
  const validNumbers = numbers
    .map(num => parseInt(num, 10))
    .filter(num => !isNaN(num) && num >= 0);

  if (validNumbers.length === 0) return 1;

  // If only one number, use it
  if (validNumbers.length === 1) {
    return validNumbers[0] === 0 ? 0 : validNumbers[0];
  }

  // Multiple numbers: prefer numbers that look like episode numbers (1-999 range)
  const episodeLikeNumbers = validNumbers.filter(num => num >= 1 && num <= 999);
  if (episodeLikeNumbers.length > 0) {
    // Use the first episode-like number
    return episodeLikeNumbers[0];
  }

  // Fall back to first number if no episode-like numbers found
  return validNumbers[0] === 0 ? 0 : validNumbers[0];
};

// No initial episodes data - will be loaded from API

interface PublishContentProps {
  view?: 'thumbnails' | 'content' | 'extras';
}

interface VideoThumbnailUpload {
  file: File;
  name: string;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'failed';
  error?: string;
}

export default function PublishContent({ view = 'thumbnails' }: PublishContentProps) {
  const navigate = useNavigate();
  const { showId } = useParams<{ showId: string }>();
  const { state, updateState } = usePublish();
  const { user, profile } = useAuth();
  const { sendSubtitleReuploadNotification, sendEpisodeReuploadNotification } = useSlackNotification();
  
  // Initialize state hooks first (before any early returns)
  const [selectedEpisodes, setSelectedEpisodes] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [previewSequence, setPreviewSequence] = useState<number | null>(null);
  const [episodePreviews, setEpisodePreviews] = useState<Record<number, EpisodePreview>>({});

  // Active/Inactive episodes state
  const [contentTab, setContentTab] = useState<'active' | 'inactive'>('active');
  const [inactiveEpisodes, setInactiveEpisodes] = useState<any[]>([]);

  // Confirmation dialog state
  const [showInactiveConfirm, setShowInactiveConfirm] = useState(false);
  const [pendingInactiveAction, setPendingInactiveAction] = useState<{
    type: 'single' | 'multi';
    episodeIds: string[];
    episodeName?: string;
  } | null>(null);
  
  // Enhanced thumbnail state
  const [show, setShow] = useState<Show | null>(null);
  const [metaDataList, setMetaDataList] = useState<any[]>([]);
  const [updatedMetaDataList, setUpdatedMetaDataList] = useState<any[]>([]);
  const [videoThumbnails, setVideoThumbnails] = useState<VideoThumbnailUpload[]>([]);
  const [videoDropKey, setVideoDropKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showName, setShowName] = useState<string>('Untitled Show');
  const [editingVideoVariant, setEditingVideoVariant] = useState<any | null>(null);
  
  // Reel upload state
  const [reelEpisodes, setReelEpisodes] = useState<EpisodeUploadStatus[]>([]);

  // Playable links live on the reel, not the episode, so they are fetched separately and keyed by sequence.
  const refreshEpisodePreviews = useCallback(async (id: string) => {
    try {
      const previews = await reelUploadService.getEpisodePreviews(id);
      setEpisodePreviews(previews.reduce<Record<number, EpisodePreview>>((bySequence, preview) => {
        bySequence[preview.sequence] = preview;
        return bySequence;
      }, {}));
    } catch (error) {
      console.error('Error fetching episode previews:', error);
    }
  }, []);

  const previewableEpisodes = useMemo<PreviewableEpisode[]>(() =>
    [...reelEpisodes]
      .sort((a, b) => a.sequence - b.sequence)
      .reduce<PreviewableEpisode[]>((playable, episode) => {
        const preview = episodePreviews[episode.sequence];
        if (preview?.videoUrl) {
          playable.push({
            sequence: episode.sequence,
            title: `${episode.sequence === 0 ? 'Trailer' : `Episode ${episode.sequence}`} · ${episode.name}`,
            videoUrl: preview.videoUrl,
            renditions: preview.renditions,
            subtitleUrl: preview.subtitleUrl,
          });
        }
        return playable;
      }, []),
    [reelEpisodes, episodePreviews]);
  // Track filename being uploaded for re-upload indication (sequence -> {filename, status})
  const [uploadingFilenames, setUploadingFilenames] = useState<Record<number, { filename: string; status: 'uploading' | 'success' | 'failed' }>>({});

  // File input ref for re-upload
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [reuploadEpisode, setReuploadEpisode] = useState<EpisodeUploadStatus | null>(null);

  // Mock user permissions
  const isDashtoonUser = true;
  const userCanEdit = true;
  
  // Get user email for uploads
  const userEmail = user?.email || profile?.email || '';

  // Load existing metadata when component mounts
  useEffect(() => {
    const loadMetadata = async () => {
      // Wait for user authentication and only load for thumbnails view
      if (!user && !profile) return; // Wait for authentication
      if (!showId || view !== 'thumbnails') return; // Only load for thumbnails view

      try {
        setLoading(true);
        // Use the correct API pattern from showService
        const showResponse = await fetchShowMetadata(showId, [
          ShowMetaDataType.REELS_THUMBNAIL_4X5,
          ShowMetaDataType.REELS_THUMBNAIL_5x8,
          ShowMetaDataType.REELS_THUMBNAIL_2x1_2,
          ShowMetaDataType.THUMBNAIL_24X7,
          ShowMetaDataType.REELS_VIDEO_THUMBNAIL_9X16
        ]);

        console.log('Thumbnails page - Show response:', showResponse); // Debug log

        // Store the full show object to access thumbnailVariants
        setShow(showResponse);

        // Extract metadata from the show object
        const metadata = showResponse.metadata || [];

        console.log('Thumbnails page - Extracted metadata:', metadata); // Debug log
        console.log('Thumbnails page - Thumbnail variants:', showResponse.thumbnailVariants); // Debug log

        setMetaDataList(metadata);
      } catch (error) {
        console.error('Error loading metadata:', error);
        // Don't show error toast for now - might be expected for new shows
      } finally {
        setLoading(false);
      }
    };

    loadMetadata();
  }, [showId, view, user, profile]);

  // Load reel episodes when component mounts and we're in content view
  useEffect(() => {
    const loadReelEpisodes = async () => {
      if (!showId || view !== 'content') return;
      
      try {
        const { episodes: episodesData, showName: fetchedShowName } = await reelUploadService.fetchEpisodesWithStatus(showId);
        console.log('📺 Episodes data from fetchEpisodesWithStatus:', episodesData);
        console.log('📺 Show name:', fetchedShowName);
        setReelEpisodes(episodesData);
        setShowName(fetchedShowName);
        await refreshEpisodePreviews(showId);
      } catch (error) {
        console.error('Error fetching reel episodes:', error);
      }
    };

    loadReelEpisodes();
  }, [showId, view, refreshEpisodePreviews]);

  // Load inactive episodes when switching to inactive tab
  useEffect(() => {
    const loadInactiveEpisodes = async () => {
      if (!showId || view !== 'content' || contentTab !== 'inactive') return;

      try {
        const inactiveData = await reelUploadService.fetchInactiveEpisodes(showId);
        setInactiveEpisodes(inactiveData);
      } catch (error) {
        console.error('Error fetching inactive episodes:', error);
        setInactiveEpisodes([]);
      }
    };

    loadInactiveEpisodes();
  }, [showId, view, contentTab]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    setSelectedFiles(Array.from(e.dataTransfer.files));
    // Handle file drop
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  const handleFileUpload = async (files: File[]) => {
    setSelectedFiles(files);
    
    const videoFiles = files.filter(file => file.type.startsWith('video/'));
    const subtitleFiles = files.filter(file => file.name.endsWith('.srt'));
    
    // Handle video files as reel uploads (automatically detects single vs bulk)
    if (videoFiles.length > 0) {
      if (videoFiles.length === 1) {
        toast.info('Single episode upload detected');
        await handleReelUploads(videoFiles);
      } else {
        toast.info(`Bulk upload detected - processing ${videoFiles.length} episodes`);
        await handleReelUploads(videoFiles);
      }
    }
    
    // Handle subtitle files
    if (subtitleFiles.length > 0) {
      await handleSubtitleUploads(subtitleFiles);
    }
    
    setSelectedFiles([]);
  };

  const handleSubtitleUploads = async (subtitleFiles: File[]) => {
    if (!userEmail || !showId) {
      toast.error('User authentication required for subtitle upload');
      return;
    }

    // File Organization & Parsing
    const subtitleMatches: Array<{
      file: File;
      episode: EpisodeUploadStatus;
      sequence: number;
    }> = [];

    for (const file of subtitleFiles) {
      // Parse filename to extract episode sequence number
      const fileName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension

      // Use comprehensive sequence parsing function
      const sequence = parseSequence(fileName);
      console.log(`🔢 Subtitle file: "${file.name}" → fileName: "${fileName}" → sequence: ${sequence}`);

      if (sequence !== null) {
        // Match subtitle file to existing episode by sequence
        const matchingEpisode = reelEpisodes.find(ep => ep.sequence === sequence);
        if (matchingEpisode) {
          subtitleMatches.push({
            file,
            episode: matchingEpisode,
            sequence
          });
        } else {
          toast.warning(`No matching episode found for ${file.name} (sequence ${sequence})`);
        }
      } else {
        toast.warning(`Could not parse sequence number from ${file.name}`);
      }
    }

    if (subtitleMatches.length === 0) {
      toast.error('No subtitle files matched existing episodes');
      return;
    }

    toast.info(`Processing ${subtitleMatches.length} subtitle files...`);

    // Get user info for Slack notification
    const userName = user?.displayName || profile?.full_name || user?.email || 'Unknown User';
    const userId = user?.uid || profile?.id || 'unknown';

    // Sequential Upload (not parallel)
    let successCount = 0;
    for (const match of subtitleMatches) {
      try {
        // File Processing - read as ArrayBuffer, convert to base64 (matching studio pattern)
        const arrayBuffer = await match.file.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        const byteArray = Array.from(uint8Array);
        const base64String = btoa(byteArray.map(byte => String.fromCharCode(byte)).join(''));

        // Upload subtitle
        const subtitleUpdateRequest = {
          showId: showId!,
          episodeId: match.episode.id,
          sequence: match.sequence,
          file: base64String
        };

        await reelUploadService.uploadSubtitle(subtitleUpdateRequest);

        toast.success(`Subtitle uploaded for ${match.episode.sequence === 0 ? 'Trailer' : `Episode ${match.episode.sequence}`}`);
        successCount++;

        // Send Slack notification for subtitle re-upload (only if episode already has subtitle status)
        const isSubtitleReupload = match.episode.subtitleStatus === 'SUBTITLE_LIVE';
        if (isSubtitleReupload) {
          try {
            await sendSubtitleReuploadNotification(showId, showName, userName, userId, match.episode.id);
          } catch (error) {
            console.warn(`Failed to send Slack notification for subtitle reupload for ${match.episode.sequence === 0 ? 'Trailer' : `Episode ${match.episode.sequence}`}:`, error);
          }
        }
      } catch (error) {
        const episodeName = match.episode.sequence === 0 ? 'Trailer' : `Episode ${match.episode.sequence}`;
        console.error(`Failed to upload subtitle for ${episodeName}:`, error);
        toast.error(`Failed to upload subtitle for ${episodeName}`);
      }
    }

    toast.success(`Subtitles uploaded successfully: ${successCount}/${subtitleMatches.length}`);
  };

  const handleReelUploads = async (videoFiles: File[]) => {
    if (!userEmail || !showId) {
      toast.error('User authentication required for reel upload');
      return;
    }

    // Get user info for Slack notification
    const userName = user?.displayName || profile?.full_name || user?.email || 'Unknown User';
    const userId = user?.uid || profile?.id || 'unknown';

    // Sort files by sequence number to ensure sequential uploads
    const sortedFiles = [...videoFiles].sort((a, b) => {
      const aSequence = parseSequence(a.name.replace(/\.[^/.]+$/, ''));
      const bSequence = parseSequence(b.name.replace(/\.[^/.]+$/, ''));
      return aSequence - bSequence;
    });

    for (const file of sortedFiles) {
      let matchingEpisode: EpisodeUploadStatus | undefined;
      let toastId: string;
      let isReupload = false; // Track if this is a re-upload

      try {
        // Validate video file
        const validation = fileUploadService.validateVideoFile(file);
        if (!validation.valid) {
          toast.error(`${file.name}: ${validation.error}`);
          continue;
        }

        // Parse filename to get sequence number (no name-based matching)
        const fileName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
        const sequence = parseSequence(fileName);
        console.log(`🔢 Video file: "${file.name}" → fileName: "${fileName}" → sequence: ${sequence}`);

        // Get fresh episodes data from API to ensure we have the latest state
        console.log(`🔄 Fetching fresh episodes data for validation...`);
        let currentEpisodes = reelEpisodes;
        try {
          const { episodes: freshEpisodes } = await reelUploadService.fetchEpisodesWithStatus(showId);
          currentEpisodes = freshEpisodes;
          setReelEpisodes(freshEpisodes);
          console.log(`✅ Fresh episodes fetched. Current episodes:`, freshEpisodes.map(ep => ({ sequence: ep.sequence, name: ep.name })));
        } catch (error) {
          console.warn(`Failed to fetch fresh episodes, using stale data:`, error);
        }

        // Find matching episode by sequence number only
        matchingEpisode = currentEpisodes.find(ep => ep.sequence === sequence);

        // If we found a matching episode, this is a re-upload
        if (matchingEpisode) {
          isReupload = true;
          console.log(`🔄 Re-upload detected for sequence ${sequence}: ${matchingEpisode.name}`);

          // Show the new filename being uploaded at this position
          setUploadingFilenames(prev => ({ ...prev, [sequence]: { filename: file.name, status: 'uploading' } }));
        }

        // If no matching episode found, validate sequential creation
        if (!matchingEpisode) {
          // Skip validation for trailer (sequence 0)
          if (sequence !== 0) {
            // Get the highest existing sequence number using fresh data
            const episodesWithSequence = currentEpisodes.filter(ep => ep.sequence > 0);
            const maxSequence = episodesWithSequence.length > 0
              ? Math.max(...episodesWithSequence.map(ep => ep.sequence))
              : 0;

            const expectedSequence = maxSequence + 1;

            // Check if user is trying to skip episodes
            if (sequence > expectedSequence) {
              toast.error(
                `Please upload episodes sequentially. Next episode should be ${expectedSequence}, but you're trying to upload Episode ${sequence}. Upload Episode ${expectedSequence} first.`
              );
              continue; // Skip this file and move to next
            }
          }

          // Create new episode
          const episodeName = fileName.replace(/_/g, ' '); // Convert underscores back to spaces
          console.log(`📝 Creating new episode: "${episodeName}" with sequence ${sequence}`);

          // Use the parsed sequence number
          const newSequence = sequence;
          
          try {
            toast.info(`Creating new episode: ${episodeName}`);
            
            // Create episode via API (Episode Creation → File Upload → Job Creation)
            const episodeData = {
              showId: showId!,
              name: episodeName,
              sequence: newSequence,
              releaseDate: Date.now(),
              thumbNailImageData: null,
              ...(profile?.email && { createdBy: profile.email })
            };

            // Use trailer endpoint for sequence 0, regular endpoint for others
            const createdEpisode = newSequence === 0 
              ? await reelUploadService.createTrailerEpisode(episodeData)
              : await reelUploadService.createEpisode(episodeData);
            
            // Create local episode object with real ID from API
            matchingEpisode = {
              id: createdEpisode.id,
              name: createdEpisode.name,
              sequence: createdEpisode.sequence,
              status: 'NO REEL' as const
            };
            
            // Add to local state and update current episodes for this iteration
            setReelEpisodes(prev => [...prev, matchingEpisode!]);
            currentEpisodes = [...currentEpisodes, matchingEpisode!];

            toast.success(`Episode created successfully: ${episodeName}`);
          } catch (error) {
            console.error('Failed to create episode:', error);
            toast.error(`Failed to create episode: ${episodeName}`);
            continue;
          }
        }

        // Set toast ID for this episode's upload process
        toastId = `upload-${matchingEpisode.id}`;

        // Update status to indicate upload is starting
        setReelEpisodes(prev => prev.map(ep =>
          ep.id === matchingEpisode!.id ? { ...ep, status: 'PROCESSING' as const } : ep
        ));

        toast.loading(`Preparing upload for ${matchingEpisode.sequence === 0 ? 'Trailer' : `Episode ${matchingEpisode.sequence}`}...`, { id: toastId });

        // Get signed URL
        const fileName_formatted = `${showId}_${matchingEpisode.name.replace(' ', '_')}.mp4`;
        const urlResponse = await reelUploadService.getGeneratedUrlForReel(fileName_formatted);

        // Upload to Azure Blob
        await reelUploadService.uploadFileToGeneratedUrl(
          urlResponse.signedUrl,
          file,
          {
            'Content-Type': 'video/mp4',
            'x-ms-blob-type': 'BlockBlob',
          },
          (progress) => {
            const episodeName = matchingEpisode?.sequence === 0 ? 'Trailer' : `Episode ${matchingEpisode?.sequence}`;
            toast.loading(`Uploading ${episodeName}... ${Math.round(progress)}%`, { id: toastId });
          }
        );

        // Update status after upload
        setReelEpisodes(prev => prev.map(ep => 
          ep.id === matchingEpisode!.id ? { ...ep, status: 'UPLOADED' as const } : ep
        ));

        // Create upload job
        const reelUrl = urlResponse.signedUrl.split('?')[0];
        console.log('About to create upload job with:', { 
          reelUrl, 
          showId, 
          episodeId: matchingEpisode.id, 
          sequence: matchingEpisode.sequence, 
          createdBy: userEmail 
        });
        
        await reelUploadService.createUploadJob({
          reelUrl,
          showId,
          episodeId: matchingEpisode.id,
          sequence: matchingEpisode.sequence,
          createdBy: userEmail
        });

        // Final status after job creation
        setReelEpisodes(prev => prev.map(ep => 
          ep.id === matchingEpisode!.id ? { ...ep, status: 'COMPLETED' as const } : ep
        ));

        toast.success(`Reel uploaded successfully for ${matchingEpisode.sequence === 0 ? 'Trailer' : `Episode ${matchingEpisode.sequence}`}!`, { id: toastId });

        // Update uploading filename status to success
        if (isReupload) {
          setUploadingFilenames(prev => {
            if (prev[matchingEpisode!.sequence]) {
              return { ...prev, [matchingEpisode!.sequence]: { ...prev[matchingEpisode!.sequence], status: 'success' } };
            }
            return prev;
          });
        }

        // Send Slack notification only for re-uploads (not first uploads)
        if (isReupload) {
          try {
            await sendEpisodeReuploadNotification(showId, showName, userName, userId, matchingEpisode.id);
          } catch (error) {
            console.warn(`Failed to send Slack notification for ${matchingEpisode.sequence === 0 ? 'Trailer' : `Episode ${matchingEpisode.sequence}`}:`, error);
          }
        }


      } catch (error) {
        console.error('Reel upload failed for', file.name, ':', error);

        // Update status to failed if we have a matching episode
        if (matchingEpisode) {
          setReelEpisodes(prev => prev.map(ep =>
            ep.id === matchingEpisode!.id ? { ...ep, status: 'FAILED' as const } : ep
          ));

          // Update uploading filename status to failed
          if (isReupload) {
            setUploadingFilenames(prev => {
              if (prev[matchingEpisode!.sequence]) {
                return { ...prev, [matchingEpisode!.sequence]: { ...prev[matchingEpisode!.sequence], status: 'failed' } };
              }
              return prev;
            });
          }
        }

        if (error instanceof Error) {
          toast.error(`Failed to upload reel for ${file.name}: ${error.message}`);
        } else {
          toast.error(`Failed to upload reel for ${file.name}`);
        }
      }
    }

    // Refresh reel episodes to update status
    try {
      const { episodes: episodesData, showName: fetchedShowName } = await reelUploadService.fetchEpisodesWithStatus(showId);
      setReelEpisodes(episodesData);
      setShowName(fetchedShowName);
      await refreshEpisodePreviews(showId);
    } catch (error) {
      console.error('Error refreshing reel episodes:', error);
    }
  };

  const handleReuploadEpisode = async (episode: EpisodeUploadStatus, file: File) => {
    if (!userEmail || !showId) {
      toast.error('User authentication required for reel upload');
      return;
    }

    const toastId = `reupload-${episode.id}`;

    try {
      // Validate video file
      const validation = fileUploadService.validateVideoFile(file);
      if (!validation.valid) {
        toast.error(`${file.name}: ${validation.error}`);
        return;
      }

      // Show uploading status
      setUploadingFilenames(prev => ({
        ...prev,
        [episode.sequence]: { filename: file.name, status: 'uploading' }
      }));

      // Update episode status
      setReelEpisodes(prev => prev.map(ep =>
        ep.id === episode.id ? { ...ep, status: 'PROCESSING' as const } : ep
      ));

      toast.loading(`Preparing upload for ${episode.sequence === 0 ? 'Trailer' : `Episode ${episode.sequence}`}...`, { id: toastId });

      // Get signed URL
      const fileName_formatted = `${showId}_${episode.name.replace(' ', '_')}.mp4`;
      const urlResponse = await reelUploadService.getGeneratedUrlForReel(fileName_formatted);

      // Upload to Azure Blob with progress
      await reelUploadService.uploadFileToGeneratedUrl(
        urlResponse.signedUrl,
        file,
        {
          'Content-Type': 'video/mp4',
          'x-ms-blob-type': 'BlockBlob',
        },
        (progress) => {
          const episodeName = episode.sequence === 0 ? 'Trailer' : `Episode ${episode.sequence}`;
          toast.loading(`Uploading ${episodeName}... ${Math.round(progress)}%`, { id: toastId });
        }
      );

      // Update status after upload
      setReelEpisodes(prev => prev.map(ep =>
        ep.id === episode.id ? { ...ep, status: 'UPLOADED' as const } : ep
      ));

      // Create upload job
      const reelUrl = urlResponse.signedUrl.split('?')[0];
      await reelUploadService.createUploadJob({
        reelUrl,
        showId,
        episodeId: episode.id,
        sequence: episode.sequence,
        createdBy: userEmail
      });

      // Final status after job creation
      setReelEpisodes(prev => prev.map(ep =>
        ep.id === episode.id ? { ...ep, status: 'COMPLETED' as const } : ep
      ));

      toast.success(`Reel re-uploaded successfully for ${episode.sequence === 0 ? 'Trailer' : `Episode ${episode.sequence}`}!`, { id: toastId });

      // Update uploading filename status to success
      setUploadingFilenames(prev => ({
        ...prev,
        [episode.sequence]: { filename: file.name, status: 'success' }
      }));

      // Send Slack notification for re-upload
      const userName = user?.displayName || profile?.full_name || user?.email || 'Unknown User';
      const userId = user?.uid || profile?.id || 'unknown';

      try {
        await sendEpisodeReuploadNotification(showId, showName, userName, userId, episode.id);
      } catch (error) {
        console.warn(`Failed to send Slack notification for ${episode.sequence === 0 ? 'Trailer' : `Episode ${episode.sequence}`}:`, error);
      }

    } catch (error) {
      console.error('Reel re-upload failed for', file.name, ':', error);

      // Update status to failed
      setReelEpisodes(prev => prev.map(ep =>
        ep.id === episode.id ? { ...ep, status: 'FAILED' as const } : ep
      ));

      // Update uploading filename status to failed
      setUploadingFilenames(prev => ({
        ...prev,
        [episode.sequence]: { filename: file.name, status: 'failed' }
      }));

      if (error instanceof Error) {
        toast.error(`Failed to re-upload reel: ${error.message}`, { id: toastId });
      } else {
        toast.error(`Failed to re-upload reel for ${file.name}`, { id: toastId });
      }
    }
  };

  // Helper function to refresh episodes
  const fetchEpisodes = async () => {
    if (!showId) return;
    try {
      const { episodes: episodesData } = await reelUploadService.fetchEpisodesWithStatus(showId);
      setReelEpisodes(episodesData);
    } catch (error) {
      console.error('Error refreshing episodes:', error);
    }
  };

  const handleDeleteEpisode = async (id: string) => {
    // For reel episodes, we need to call the delete API
    if (showId) {
      const episodeToDelete = reelEpisodes.find(ep => ep.id === id);
      if (episodeToDelete) {
        // Show confirmation dialog
        setPendingInactiveAction({
          type: 'single',
          episodeIds: [id],
          episodeName: episodeToDelete.name
        });
        setShowInactiveConfirm(true);
      }
    }
  };

  const confirmInactiveAction = async () => {
    if (!pendingInactiveAction || !showId) return;

    try {
      await reelUploadService.deleteEpisodes(showId, pendingInactiveAction.episodeIds);

      const count = pendingInactiveAction.episodeIds.length;
      toast.success(
        count === 1
          ? 'Episode marked as inactive'
          : `${count} episodes marked as inactive`
      );

      await fetchEpisodes();
      setSelectedEpisodes([]);
      setShowInactiveConfirm(false);
      setPendingInactiveAction(null);
    } catch (error) {
      console.error('Error marking episodes as inactive:', error);
      toast.error('Failed to mark episodes as inactive');
    }
  };

  const handleMultiAction = async (action: string) => {
    if (selectedEpisodes.length === 0) {
      toast.error('Please select items first');
      return;
    }

    if (action === 'Inactive') {
      // Show confirmation dialog
      setPendingInactiveAction({
        type: 'multi',
        episodeIds: selectedEpisodes
      });
      setShowInactiveConfirm(true);
      return;
    }

    toast.success(`${action} action applied to ${selectedEpisodes.length} items`);
    setSelectedEpisodes([]);
  };

  // Enhanced thumbnail functionality with real API calls
  const handleFileUploadForThumbnail = async (file: File, type: ShowMetaDataType) => {
    if (!showId) {
      toast.error('Show ID is required for thumbnail upload');
      return;
    }

    // Special handling for video thumbnail 9:16
    if (type === ShowMetaDataType.REELS_VIDEO_THUMBNAIL_9X16) {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        // Aspect ratio check (9:16)
        const aspectRatio = video.videoWidth / video.videoHeight;
        const expected = 9 / 16;
        if (Math.abs(aspectRatio - expected) > 0.01) {
          toast.error('Video aspect ratio must be 9:16');
          setVideoDropKey(prev => prev + 1);
          return;
        }
        // If valid, proceed as usual
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          const base64StringWithoutPrefix = base64String.substring(base64String.indexOf(',') + 1);
          setUpdatedMetaDataList(prevList => {
            const updatedList = prevList.map(item => {
              if (item.type === type) {
                return { ...item, value: base64StringWithoutPrefix };
              }
              return item;
            });
            if (!updatedList.some(item => item.type === type)) {
              updatedList.push({ type, value: base64StringWithoutPrefix });
            }
            return [...updatedList];
          });
        };
        reader.onerror = () => {
          console.error('There was an error reading the file!');
          setVideoDropKey(prev => prev + 1);
        };
        reader.readAsDataURL(file);
      };
      video.onerror = () => {
        toast.error('Could not load video file.');
        setVideoDropKey(prev => prev + 1);
      };
      video.src = URL.createObjectURL(file);
      return;
    }

    // Validate file
    const validation = fileUploadService.validateThumbnailFile(file);
    if (!validation.valid) {
      toast.error(validation.error || 'Invalid file');
      return;
    }

    try {
      setLoading(true);

      // Convert to base64 following studio pattern
      const base64Data = await fileUploadService.uploadThumbnailImage(file);

      // Update metadata via API with optional label support
      // For thumbnail variants, label can be added by user (defaults to null)
      await updateShowMetadata(showId, [{
        type,
        value: base64Data,
        label: undefined // Label can be set via Edit dialog after upload
      }]);

      // Reload show data to get updated variants from PostgreSQL
      const updatedShow = await fetchShowMetadata(showId, [
        ShowMetaDataType.REELS_THUMBNAIL_4X5,
        ShowMetaDataType.REELS_THUMBNAIL_5x8,
        ShowMetaDataType.REELS_THUMBNAIL_2x1_2,
        ShowMetaDataType.THUMBNAIL_24X7,
        ShowMetaDataType.REELS_VIDEO_THUMBNAIL_9X16
      ]);
      setShow(updatedShow);

      toast.success('Thumbnail variant uploaded successfully');
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      toast.error('Failed to upload thumbnail');
    } finally {
      setLoading(false);
    }
  };

  const handleMultipleFilesUploadForThumbnail = async (files: File[], type: ShowMetaDataType) => {
    if (!showId || files.length === 0) return;

    try {
      setLoading(true);
      let successCount = 0;
      let failCount = 0;

      // Upload each file sequentially
      for (const file of files) {
        try {
          const validation = fileUploadService.validateThumbnailFile(file);
          if (!validation.valid) {
            console.error(`Invalid file ${file.name}:`, validation.error);
            failCount++;
            continue;
          }

          const base64Data = await fileUploadService.uploadThumbnailImage(file);

          await updateShowMetadata(showId, [{
            type,
            value: base64Data,
            label: undefined
          }]);

          successCount++;
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
          failCount++;
        }
      }

      // Reload show data once after all uploads
      const updatedShow = await fetchShowMetadata(showId, [
        ShowMetaDataType.REELS_THUMBNAIL_4X5,
        ShowMetaDataType.REELS_THUMBNAIL_5x8,
        ShowMetaDataType.REELS_THUMBNAIL_2x1_2,
        ShowMetaDataType.THUMBNAIL_24X7,
        ShowMetaDataType.REELS_VIDEO_THUMBNAIL_9X16
      ]);
      setShow(updatedShow);

      // Show summary toast
      if (successCount > 0 && failCount === 0) {
        toast.success(`Successfully uploaded ${successCount} thumbnail variant${successCount > 1 ? 's' : ''}`);
      } else if (successCount > 0 && failCount > 0) {
        toast.warning(`Uploaded ${successCount} variant${successCount > 1 ? 's' : ''}, ${failCount} failed`);
      } else {
        toast.error('Failed to upload thumbnails');
      }
    } catch (error) {
      console.error('Error uploading thumbnails:', error);
      toast.error('Failed to upload thumbnails');
    } finally {
      setLoading(false);
    }
  };

  const handleFileRemove = async (type: ShowMetaDataType) => {
    if (!showId) {
      toast.error('Show ID is required');
      return;
    }

    try {
      setLoading(true);
      
      // Remove from API
      await updateShowMetadata(showId, [{ type, value: '' }]);
      
      // Update both local states
      setUpdatedMetaDataList(prev => prev.filter(item => item.type !== type));
      setMetaDataList(prev => prev.filter(item => item.type !== type));
      
      toast.success('Thumbnail removed successfully');
    } catch (error) {
      console.error('Error removing thumbnail:', error);
      toast.error('Failed to remove thumbnail');
    } finally {
      setLoading(false);
    }
  };

  // Utility function to reload metadata
  const fetchMetaDataForShow = async () => {
    if (!showId) return;
    try {
      const showResponse = await fetchShowMetadata(showId, [
        ShowMetaDataType.REELS_THUMBNAIL_4X5,
        ShowMetaDataType.REELS_THUMBNAIL_5x8,
        ShowMetaDataType.REELS_THUMBNAIL_2x1_2,
        ShowMetaDataType.THUMBNAIL_24X7,
        ShowMetaDataType.REELS_VIDEO_THUMBNAIL_9X16
      ]);
      setMetaDataList(showResponse.metadata || []);
      setShow(showResponse); // Update show object with latest thumbnailVariants and transcoded status
    } catch (error) {
      console.error('Error reloading metadata:', error);
    }
  };

  const handleDeleteVideoThumbnail = async (_value: string) => {
    if (!userCanEdit || !showId) return;
    try {
      setLoading(true);
      // Delete all video thumbnails of this type
      await deleteShowMetadata(showId, ShowMetaDataType.REELS_VIDEO_THUMBNAIL_9X16);
      await fetchMetaDataForShow();
      setLoading(false);
      toast.success('Video thumbnails deleted');
    } catch (error) {
      setLoading(false);
      toast.error('Failed to delete video thumbnails');
    }
  };

  const handleBulkVideoDrop = (files: File[]) => {
    if (!userCanEdit) return;
    const newThumbnails: VideoThumbnailUpload[] = [];
    let filesProcessed = 0;
    if (files.length > 10) {
      toast.error(`Maximum 10 video thumbnails allowed at once. Selected: ${files.length} files`);
      filesProcessed++;
      return;
    }
    files.forEach(file => {
      if (!file.name.toLowerCase().endsWith('.mp4')) {
        toast.error(`${file.name}: Only .mp4 files are allowed`);
        filesProcessed++;
        return;
      }
      // File size check (15MB = 15 * 1024 * 1024 bytes)
      if (file.size > 15 * 1024 * 1024) {
        toast.error(`${file.name}: Video thumbnail must be under 15MB`);
        filesProcessed++;
        return;
      }
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        const aspectRatio = video.videoWidth / video.videoHeight;
        const expected = 9 / 16;
        if (Math.abs(aspectRatio - expected) > 0.01) {
          toast.error(`${file.name}: Video aspect ratio must be 9:16`);
        } else {
          newThumbnails.push({
            file,
            name: file.name,
            progress: 0,
            status: 'pending',
          });
        }
        filesProcessed++;
        if (filesProcessed === files.length) {
          setVideoThumbnails(prev => [...prev, ...newThumbnails]);
        }
      };
      video.onerror = () => {
        toast.error(`${file.name}: Could not load video file.`);
        filesProcessed++;
        if (filesProcessed === files.length) {
          setVideoThumbnails(prev => [...prev, ...newThumbnails]);
        }
      };
      video.src = URL.createObjectURL(file);
    });
  };

  const handleUploadAllVideoThumbnails = async () => {
    if (!showId) {
      toast.error('Show ID is required');
      return;
    }

    setLoading(true);
    setVideoThumbnails(prev => prev.map(v => v.status === 'pending' ? { ...v, status: 'uploading', progress: 0 } : v));
    const uploadedUrls: string[] = [];

    for (let i = 0; i < videoThumbnails.length; i++) {
      const thumbnail = videoThumbnails[i];
      if (thumbnail.status !== 'pending') continue;
      try {
        // 1. Get signed URL
        const { signedUrl } = await reelUploadService.getGeneratedUrlForReel(`${showId}_thumbnail_${thumbnail.name}`);
        if (!signedUrl) throw new Error('Failed to get upload URL');

        // 2. Upload file
        await reelUploadService.uploadFileToGeneratedUrl(
          signedUrl,
          thumbnail.file,
          { 'Content-Type': 'video/mp4', 'x-ms-blob-type': 'BlockBlob' },
          (progress: number) => {
            setVideoThumbnails(prev => prev.map((v, idx) => idx === i ? { ...v, progress } : v));
          }
        );

        // 3. Collect uploaded URL
        let uploadedUrl = signedUrl.split('?')[0];
        if (uploadedUrl.includes('dashdevstore.blob.core.windows.net')) {
          uploadedUrl = uploadedUrl.replace(
            'https://dashdevstore.blob.core.windows.net/',
            'https://dev-content.dashtoon.ai/'
          );
        } else if (uploadedUrl.includes('dashprodstore.blob.core.windows.net')) {
          uploadedUrl = uploadedUrl.replace(
            'https://dashprodstore.blob.core.windows.net/',
            'https://content.dashtoon.ai/'
          );
        }
        uploadedUrls.push(uploadedUrl);
        setVideoThumbnails(prev => prev.map((v, idx) => idx === i ? { ...v, status: 'success', progress: 100 } : v));
      } catch (error) {
        setVideoThumbnails(prev => prev.map((v, idx) => idx === i ? { ...v, status: 'failed', error: (error as Error).message } : v));
        toast.error(`${thumbnail.name}: Upload failed`);
      }
    }

    if (uploadedUrls.length > 0) {
      try {
        // Only send new URLs to prevent duplicate job creation in backend
        // Backend will handle merging with existing metadata
        await updateShowMetadata(showId, [{
          type: ShowMetaDataType.REELS_VIDEO_THUMBNAIL_9X16,
          value: JSON.stringify(uploadedUrls),  // Send only new URLs
        }]);

        await fetchMetaDataForShow();
        toast.success('All video thumbnails uploaded successfully');
        setVideoThumbnails([]);
      } catch (error) {
        console.error('Error updating metadata:', error);
        toast.error('Failed to update metadata');
      }
    }

    setLoading(false);
  };

  // Reel status helpers
  const getReelStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'PROCESSING': return 'bg-blue-100 text-blue-800';
      case 'UPLOADED': return 'bg-yellow-100 text-yellow-800';
      case 'FAILED': return 'bg-red-100 text-red-800';
      case 'NO REEL': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getReelStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED': return <Play className="h-4 w-4" />;
      case 'FAILED': return <X className="h-4 w-4" />;
      case 'PROCESSING': case 'UPLOADED': return <Upload className="h-4 w-4" />;
      case 'NO REEL': return <Video className="h-4 w-4" />;
      default: return null;
    }
  };

  const getSubtitleStatusColor = (subtitleStatus: string | null | undefined) => {
    switch (subtitleStatus) {
      case 'SUBTITLE_LIVE': return 'bg-green-100 text-green-800';
      case 'NO_SUBTITLE': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getSubtitleStatusText = (subtitleStatus: string | null | undefined) => {
    switch (subtitleStatus) {
      case 'SUBTITLE_LIVE': return 'Live';
      case 'NO_SUBTITLE': return 'Missing';
      default: return 'Unknown';
    }
  };

  return (
    <PublishLayout
      onContinue={() => {
        if (view === 'thumbnails') {
          navigate(showId ? `/publish/${showId}/content` : '/publish/content');
        } else if (view === 'content') {
          navigate(showId ? `/publish/${showId}/preview` : '/publish/preview');
        }
      }}
    >
      <div className="max-w-[720px] mx-auto px-6 py-8">
          {/* Page Title */}
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-base font-semibold">
              {view === 'thumbnails' && 'Thumbnails'}
              {view === 'content' && 'Content'}
              {view === 'extras' && 'Extras'}
            </h2>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowShareModal(true)}>
                <Users className="h-4 w-4 mr-2" />
                Invite
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  toast.success('Link copied to clipboard');
                }}
              >
                <Link2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="space-y-8">
            {view === 'thumbnails' && (
              <div className="space-y-8">
                {isDashtoonUser && (
                  <div className="space-y-8">
                    {/* Reels Thumbnails Section */}
                    <div>
                      <h3 className="text-lg font-semibold mb-6">Reels Thumbnails</h3>
                      <div className="space-y-6">
                        {/* Portrait thumbnails - 4:5 and 5:8 */}
                        <div className="space-y-4">
                          {/* 4:5 Thumbnail Variants Section */}
                          <ThumbnailVariantsSection
                            title="Reels thumbnail (4:5)"
                            metadataType="THUMBNAIL_4X5"
                            variants={show?.thumbnailVariants?.['THUMBNAIL_4X5'] || []}
                            aspectRatio="4/5"
                            showId={showId!}
                            uploadArea={
                              <DropArea
                                imageUrl={undefined}
                                enforceWidth={false}
                                enforceHeight={false}
                                requiredHeight={REELS_THUMBNAIL_4X5_HEIGHT}
                                requiredWidth={REELS_THUMBNAIL_4X5_WIDTH}
                                multiple={true}
                                accept=".jpg,.png,.jpeg"
                                label={`Upload or drag and drop (multiple files supported)\n${REELS_THUMBNAIL_4X5_WIDTH}px x ${REELS_THUMBNAIL_4X5_HEIGHT}px\n.png, .jpg or .jpeg formats`}
                                onFileUpload={file =>
                                  handleFileUploadForThumbnail(file, ShowMetaDataType.REELS_THUMBNAIL_4X5)
                                }
                                onMultipleFilesUpload={files =>
                                  handleMultipleFilesUploadForThumbnail(files, ShowMetaDataType.REELS_THUMBNAIL_4X5)
                                }
                                onFileDelete={() =>
                                  handleFileRemove(ShowMetaDataType.REELS_THUMBNAIL_4X5)
                                }
                                sizeScale={4}
                                padding={'0px'}
                              />
                            }
                            onEditVariant={async (variantId, label, isActive) => {
                              try {
                                await updateThumbnailVariant(showId!, variantId, label, isActive);
                                const updated = await fetchShowMetadata(showId!, [
                                  ShowMetaDataType.REELS_THUMBNAIL_4X5,
                                  ShowMetaDataType.REELS_THUMBNAIL_5x8,
                                  ShowMetaDataType.REELS_THUMBNAIL_2x1_2,
                                  ShowMetaDataType.THUMBNAIL_24X7,
                                  ShowMetaDataType.REELS_VIDEO_THUMBNAIL_9X16
                                ]);
                                setShow(updated);
                              } catch (error) {
                                console.error('Failed to update variant:', error);
                              }
                            }}
                          />

                          {/* 5:8 Thumbnail Variants Section */}
                          <ThumbnailVariantsSection
                            title="Reels thumbnail (5:8)"
                            metadataType="THUMBNAIL_5X8"
                            variants={show?.thumbnailVariants?.['THUMBNAIL_5X8'] || []}
                            aspectRatio="5/8"
                            showId={showId!}
                            uploadArea={
                              <DropArea
                                imageUrl={undefined}
                                enforceWidth={false}
                                enforceHeight={false}
                                requiredHeight={REELS_THUMBNAIL_5X8_HEIGHT * 0.85}
                                requiredWidth={REELS_THUMBNAIL_5X8_WIDTH}
                                multiple={true}
                                accept=".jpg,.png,.jpeg"
                                label={`Upload or drag and drop (multiple files supported)\n${REELS_THUMBNAIL_5X8_WIDTH}px x ${REELS_THUMBNAIL_5X8_HEIGHT}px\n.png, .jpg or .jpeg formats`}
                                onFileUpload={file =>
                                  handleFileUploadForThumbnail(file, ShowMetaDataType.REELS_THUMBNAIL_5x8)
                                }
                                onMultipleFilesUpload={files =>
                                  handleMultipleFilesUploadForThumbnail(files, ShowMetaDataType.REELS_THUMBNAIL_5x8)
                                }
                                onFileDelete={() =>
                                  handleFileRemove(ShowMetaDataType.REELS_THUMBNAIL_5x8)
                                }
                                sizeScale={4}
                                padding={'0px'}
                              />
                            }
                            onEditVariant={async (variantId, label, isActive) => {
                              try {
                                await updateThumbnailVariant(showId!, variantId, label, isActive);
                                const updated = await fetchShowMetadata(showId!, [
                                  ShowMetaDataType.REELS_THUMBNAIL_4X5,
                                  ShowMetaDataType.REELS_THUMBNAIL_5x8,
                                  ShowMetaDataType.REELS_THUMBNAIL_2x1_2,
                                  ShowMetaDataType.THUMBNAIL_24X7,
                                  ShowMetaDataType.REELS_VIDEO_THUMBNAIL_9X16
                                ]);
                                setShow(updated);
                              } catch (error) {
                                console.error('Failed to update variant:', error);
                              }
                            }}
                          />
                        </div>

                        {/* 2:1.2 Thumbnail Variants Section */}
                        <ThumbnailVariantsSection
                          title="Reels thumbnail (2:1.2)"
                          metadataType="THUMBNAIL_2X1_2"
                          variants={show?.thumbnailVariants?.['THUMBNAIL_2X1_2'] || []}
                          aspectRatio="2/1.2"
                          showId={showId!}
                          uploadArea={
                            <DropArea
                              imageUrl={undefined}
                              enforceWidth={false}
                              enforceHeight={false}
                              requiredHeight={REELS_THUMBNAIL_2X1_2HEIGHT * 1.5}
                              requiredWidth={REELS_THUMBNAIL_2X1_2WIDTH}
                              multiple={true}
                              accept=".jpg,.png,.jpeg"
                              label={`Upload or drag and drop (multiple files supported)\n${REELS_THUMBNAIL_2X1_2WIDTH}px x ${REELS_THUMBNAIL_2X1_2HEIGHT}px\n.png, .jpg or .jpeg formats`}
                              onFileUpload={file =>
                                handleFileUploadForThumbnail(file, ShowMetaDataType.REELS_THUMBNAIL_2x1_2)
                              }
                              onMultipleFilesUpload={files =>
                                handleMultipleFilesUploadForThumbnail(files, ShowMetaDataType.REELS_THUMBNAIL_2x1_2)
                              }
                              onFileDelete={() =>
                                handleFileRemove(ShowMetaDataType.REELS_THUMBNAIL_2x1_2)
                              }
                              sizeScale={4}
                              padding={'0px'}
                            />
                          }
                          onEditVariant={async (variantId, label, enabled) => {
                            try {
                              await updateThumbnailVariant(showId!, variantId, label, enabled);
                              const updated = await fetchShowMetadata(showId!, [
                                ShowMetaDataType.BANNER_THUMBNAIL_V2,
                                ShowMetaDataType.WIDGET_THUMBNAIL_V2,
                                ShowMetaDataType.DETAIL_PAGE_THUMBNAIL_V2,
                                ShowMetaDataType.CONTINUE_READING_THUMBNAIL_V2,
                                ShowMetaDataType.POST_CREDIT_THUMBNAILS,
                                ShowMetaDataType.END_CREDIT_PANELS,
                                ShowMetaDataType.REELS_THUMBNAIL_4X5,
                                ShowMetaDataType.REELS_THUMBNAIL_5x8,
                                ShowMetaDataType.REELS_THUMBNAIL_2x1_2,
                                ShowMetaDataType.THUMBNAIL_24X7
                              ]);
                              setShow(updated);
                            } catch (error) {
                              console.error('Failed to update variant:', error);
                            }
                          }}
                        />

                        {/* Video title thumbnail - 24:7 (with A/B testing) */}
                        <ThumbnailVariantsSection
                          title="Video title (3.43:1)"
                          metadataType="THUMBNAIL_24X7"
                          variants={show?.thumbnailVariants?.['THUMBNAIL_24X7'] || []}
                          aspectRatio="24/7"
                          showId={showId!}
                          uploadArea={
                            <DropArea
                              imageUrl={undefined}
                              enforceWidth={false}
                              enforceHeight={false}
                              requiredHeight={THUMBNAIL_240X70_HEIGHT * 3.23}
                              requiredWidth={THUMBNAIL_240X70_WIDTH}
                              multiple={true}
                              accept=".jpg,.png,.jpeg"
                              label={t(
                                'Upload or drag and drop (multiple files supported)\n' +
                                '240px x 70px .png or .jpg or .jpeg formats'
                              )}
                              onFileUpload={file =>
                                handleFileUploadForThumbnail(file, ShowMetaDataType.THUMBNAIL_24X7)
                              }
                              onMultipleFilesUpload={files =>
                                handleMultipleFilesUploadForThumbnail(files, ShowMetaDataType.THUMBNAIL_24X7)
                              }
                              onFileDelete={() =>
                                handleFileRemove(ShowMetaDataType.THUMBNAIL_24X7)
                              }
                              sizeScale={1}
                              padding={'0px'}
                            />
                          }
                          onEditVariant={async (variantId, label, enabled) => {
                            try {
                              await updateThumbnailVariant(showId!, variantId, label, enabled);
                              const updated = await fetchShowMetadata(showId!, [
                                ShowMetaDataType.BANNER_THUMBNAIL_V2,
                                ShowMetaDataType.WIDGET_THUMBNAIL_V2,
                                ShowMetaDataType.DETAIL_PAGE_THUMBNAIL_V2,
                                ShowMetaDataType.CONTINUE_READING_THUMBNAIL_V2,
                                ShowMetaDataType.POST_CREDIT_THUMBNAILS,
                                ShowMetaDataType.END_CREDIT_PANELS,
                                ShowMetaDataType.REELS_THUMBNAIL_4X5,
                                ShowMetaDataType.REELS_THUMBNAIL_5x8,
                                ShowMetaDataType.REELS_THUMBNAIL_2x1_2,
                                ShowMetaDataType.THUMBNAIL_24X7
                              ]);
                              setShow(updated);
                            } catch (error) {
                              console.error('Failed to update variant:', error);
                            }
                          }}
                        />
                      </div>
                    </div>

                    {/* Video Thumbnails Section */}
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Video Thumbnails (9:16)</h3>
                        
                        {/* Existing Video Thumbnails */}
                        {(!show?.thumbnailVariants?.['VIDEO_THUMBNAIL_9X16'] || show.thumbnailVariants['VIDEO_THUMBNAIL_9X16'].length === 0) ? (
                          <div className="text-sm text-muted-foreground mb-6">No video thumbnails uploaded yet.</div>
                        ) : (
                          <div className="flex flex-wrap gap-4 mb-6">
                            {show.thumbnailVariants['VIDEO_THUMBNAIL_9X16'].map((variant: any) => {
                              const isTranscoded = variant.transcoded ?? false;
                              const transformedUrl = transformVodUrlToHttp(variant.assetUrl);

                              return (
                                <div key={variant.id} className="space-y-2">
                                  <div className="relative">
                                    <video
                                      src={transformedUrl}
                                      controls
                                      className="w-[120px] h-[213px] bg-secondary border rounded-lg"
                                    />
                                    <Badge
                                      variant={isTranscoded ? "default" : "secondary"}
                                      className={`absolute top-1 right-1 text-xs ${
                                        isTranscoded
                                          ? "bg-green-600 hover:bg-green-700"
                                          : "bg-orange-500 hover:bg-orange-600"
                                      } text-white`}
                                    >
                                      {isTranscoded ? 'Transcoded' : 'Pending'}
                                    </Badge>
                                  </div>
                                  <div className="space-y-1.5 w-[120px]">
                                    <div className="flex items-center justify-end gap-2">
                                      <Badge
                                        variant={variant.isActive ? "default" : "secondary"}
                                        className={`text-xs ${
                                          variant.isActive
                                            ? "bg-green-500 text-white"
                                            : "bg-gray-500 text-white"
                                        }`}
                                      >
                                        {variant.isActive ? 'Active' : 'Inactive'}
                                      </Badge>
                                    </div>
                                    {variant.label && (
                                      <div className="text-xs font-medium truncate" title={variant.label}>
                                        {variant.label}
                                      </div>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setEditingVideoVariant(variant)}
                                      className="w-full h-7 text-xs"
                                    >
                                      <Pencil className="h-3 w-3 mr-1" />
                                      Edit
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Delete All Button */}
                        {userCanEdit && show?.thumbnailVariants?.['VIDEO_THUMBNAIL_9X16'] && show.thumbnailVariants['VIDEO_THUMBNAIL_9X16'].length > 0 && (
                          <Button
                            variant="destructive"
                            onClick={() => handleDeleteVideoThumbnail('all')}
                            className="mb-6"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete All Video Thumbnails
                          </Button>
                        )}

                        {/* Bulk Upload Section */}
                        <div className="space-y-4">
                          <h4 className="text-base font-medium">Bulk Upload Video Thumbnails</h4>
                          <DropZone onFoldersSelected={handleBulkVideoDrop} />
                          
                          {/* Upload Progress Table */}
                          {videoThumbnails.length > 0 && (
                            <div className="space-y-4">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>File Name</TableHead>
                                    <TableHead>Progress</TableHead>
                                    <TableHead>Status</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {videoThumbnails.map((thumb, idx) => (
                                    <TableRow key={thumb.name + idx}>
                                      <TableCell className="font-mono text-sm">{thumb.name}</TableCell>
                                      <TableCell>
                                        {thumb.progress ? `${Math.round(thumb.progress)}%` : ''}
                                      </TableCell>
                                      <TableCell>
                                        <Badge variant={thumb.status === 'success' ? 'default' : 'secondary'}>
                                          {thumb.status}
                                        </Badge>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                              <Button 
                                onClick={handleUploadAllVideoThumbnails}
                                disabled={loading}
                              >
                                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                Upload All
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Edit Video Variant Dialog */}
                        <EditVariantDialog
                          variant={editingVideoVariant}
                          open={editingVideoVariant !== null}
                          onClose={() => setEditingVideoVariant(null)}
                          onSave={async (variantId, label, enabled) => {
                            try {
                              await updateThumbnailVariant(showId!, variantId, label, enabled);
                              const updated = await fetchShowMetadata(showId!, [
                                ShowMetaDataType.REELS_VIDEO_THUMBNAIL_9X16
                              ]);
                              setShow(updated);
                              toast.success('Video thumbnail variant updated successfully');
                            } catch (error) {
                              console.error('Failed to update variant:', error);
                              toast.error('Failed to update video thumbnail variant');
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

              </div>
            )}

            {view === 'content' && (
              <>
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isDragging ? 'border-primary bg-primary/5' : selectedFiles.length > 0 ? 'border-primary/50' : 'border-border'
                  } hover:bg-secondary/20 hover:border-primary/50 cursor-pointer`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('file-upload')?.click()}
                >
                  {selectedFiles.length === 0 ? (
                    <div className="mx-auto w-full max-w-sm space-y-3">
                      <div className="flex items-center justify-center gap-3">
                        <Upload className="h-5 w-5 text-muted-foreground/70" />
                        <p className="text-sm text-muted-foreground/70 font-medium">
                          Drag and drop or click to upload your files
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground/80">
                        Upload video files for reels (Episode_{}.mp4) & subtitles (Episode_{}.srt)
                      </p>
                      <input
                        id="file-upload"
                        type="file"
                        multiple
                        accept=".srt,.mp4,.mov,.avi"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files) {
                            handleFileUpload(Array.from(e.target.files));
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <h3 className="text-lg font-medium">Files Ready to Upload</h3>
                      <p className="text-sm text-muted-foreground">
                        {selectedFiles.length} file(s) selected
                      </p>
                      <div className="flex justify-center gap-2 mt-4">
                        <Button variant="default" onClick={() => handleFileUpload(selectedFiles)}>
                          <Upload className="h-4 w-4 mr-2" />
                          Upload Files
                        </Button>
                        <Button variant="ghost" onClick={() => setSelectedFiles([])}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Hidden file input for single episode re-upload */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/*,.mp4,.mov,.avi"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0] && reuploadEpisode) {
                      handleReuploadEpisode(reuploadEpisode, e.target.files[0]);
                      setReuploadEpisode(null);
                      // Reset the input so the same file can be selected again
                      e.target.value = '';
                    }
                  }}
                />
              </>
            )}

            {view === 'content' && (
              <>
                {/* Active/Inactive Tabs */}
                <div className="flex border-b border-border mb-4">
                  <button
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      contentTab === 'active'
                        ? 'border-b-2 border-primary text-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    onClick={() => setContentTab('active')}
                  >
                    Active Episodes
                  </button>
                  <button
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      contentTab === 'inactive'
                        ? 'border-b-2 border-primary text-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                    onClick={() => setContentTab('inactive')}
                  >
                    Inactive Episodes
                  </button>
                </div>

                {/* Multi-select Actions */}
                {contentTab === 'active' && selectedEpisodes.length > 0 && (
                  <div className="flex items-center gap-2 my-4">
                    <span className="text-sm text-muted-foreground">
                      {selectedEpisodes.length} item{selectedEpisodes.length > 1 ? 's' : ''} selected
                    </span>
                    <Button variant="outline" size="sm" onClick={() => handleMultiAction('Replace')}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Replace
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleMultiAction('Add Thumbnail')}>
                      <ImageIcon className="h-4 w-4 mr-2" />
                      Add Thumbnail
                    </Button>
                    <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleMultiAction('Inactive')}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Inactive
                    </Button>
                  </div>
                )}

                {/* Active Episodes Table */}
                {contentTab === 'active' && (
                  <div className="rounded-lg divide-y divide-border/30">
                    {reelEpisodes.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow className="hover:bg-secondary/70">
                          <TableHead className="w-12 h-10">
                            <Checkbox
                               className="h-4 w-4 rounded border-border hover:border-primary focus:ring-1 focus:ring-ring checked:bg-primary checked:border-primary"
                               checked={selectedEpisodes.length === reelEpisodes.length}
                               onCheckedChange={(checked) => {
                                 const allIds = reelEpisodes.map(ep => ep.id);
                                 if (checked) {
                                   setSelectedEpisodes(allIds);
                                 } else {
                                   setSelectedEpisodes([]);
                                 }
                               }}
                             />
                           </TableHead>
                           <TableHead className="w-12 text-xs">#</TableHead>
                           <TableHead className="w-[160px] text-xs">Name</TableHead>
                           <TableHead className="w-[100px] text-xs">Status</TableHead>
                           <TableHead className="text-xs">Reel Status</TableHead>
                           <TableHead className="text-xs">Subtitle</TableHead>
                           <TableHead className="w-[104px] text-xs">Preview</TableHead>
                           <TableHead className="text-xs">Re-upload File</TableHead>
                           <TableHead className="w-12 text-xs"></TableHead>
                         </TableRow>
                       </TableHeader>
                       <TableBody>
                         {/* Reel Episodes - always sort by sequence for consistent display */}
                         {(() => {
                           const episodes = reelEpisodes;
                           // Always sort by sequence for consistent display
                           const displayEpisodes = [...episodes].sort((a, b) => {
                             // Trailer (sequence 0) comes first, then sort by sequence number
                             if (a.sequence === 0 && b.sequence !== 0) return -1;
                             if (b.sequence === 0 && a.sequence !== 0) return 1;
                             return a.sequence - b.sequence;
                           });

                           return displayEpisodes.map((episode) => (
                           <TableRow
                             key={episode.id}
                             className="hover:bg-secondary/20"
                           >
                             <TableCell>
                               <Checkbox
                                 className="h-4 w-4 rounded border-border hover:border-primary focus:ring-1 focus:ring-ring checked:bg-primary checked:border-primary"
                                 checked={selectedEpisodes.includes(episode.id)}
                                 onCheckedChange={(checked) => {
                                   if (checked) {
                                     setSelectedEpisodes([...selectedEpisodes, episode.id]);
                                   } else {
                                     setSelectedEpisodes(selectedEpisodes.filter(id => id !== episode.id));
                                   }
                                 }}
                               />
                             </TableCell>
                             <TableCell className="text-xs text-muted-foreground font-mono">
                               {String(episode.sequence).padStart(2, '0')}
                             </TableCell>
                             <TableCell className="text-sm truncate max-w-[160px]">
                               <div
                                className="group/name relative h-full flex items-center px-2"
                                 onDoubleClick={(e) => {
                                   e.stopPropagation();
                                   const input = e.currentTarget.querySelector('input');
                                   if (input) {
                                     input.classList.remove('hidden');
                                     input.classList.add('block');
                                     input.parentElement?.querySelector('span')?.classList.add('opacity-0');
                                     input.focus();
                                     input.select();
                                   }
                                 }}
                               >
                                <span className="flex items-center">
                                  {episode.sequence === 0 ? 'Trailer' : `Episode ${episode.sequence}`}
                                  <span className="text-xs text-muted-foreground ml-1">({episode.name})</span>
                                </span>
                                 <input
                                   type="text"
                                   defaultValue={episode.name}
                                  className="hidden absolute inset-0 w-full h-full bg-background border-0 px-2 text-sm focus:outline-none focus:ring-0 focus:bg-secondary/50"
                                   onBlur={(e) => {
                                     const newName = e.target.value.trim();
                                     if (newName && newName !== episode.name) {
                                       setReelEpisodes(prev => prev.map(ep => 
                                         ep.id === episode.id ? { ...ep, name: newName } : ep
                                       ));
                                       toast.success('Episode renamed successfully');
                                     }
                                     e.target.classList.remove('block');
                                     e.target.classList.add('hidden');
                                     e.target.parentElement?.querySelector('span')?.classList.remove('opacity-0');
                                   }}
                                   onKeyDown={(e) => {
                                     if (e.key === 'Enter') {
                                       e.currentTarget.blur();
                                     } else if (e.key === 'Escape') {
                                       e.currentTarget.value = episode.name;
                                       e.currentTarget.blur();
                                     }
                                   }}
                                 />
                               </div>
                             </TableCell>
                             <TableCell>
                               <Badge 
                                 variant="outline"
                               >
                                 Episode
                               </Badge>
                             </TableCell>
                             <TableCell>
                               <Badge 
                                 className={`text-xs ${getReelStatusColor(episode.status)}`}
                                 variant="secondary"
                               >
                                 <div className="flex items-center gap-1">
                                   {getReelStatusIcon(episode.status)}
                                   {episode.status}
                                 </div>
                               </Badge>
                             </TableCell>
                             <TableCell>
                               <Badge
                                 className={`text-xs ${getSubtitleStatusColor(episode.subtitleStatus)}`}
                                 variant="secondary"
                               >
                                 {getSubtitleStatusText(episode.subtitleStatus)}
                               </Badge>
                             </TableCell>
                             <TableCell>
                               {episodePreviews[episode.sequence]?.videoUrl ? (
                                 <Button
                                   variant="outline"
                                   size="sm"
                                   className="h-8 px-2"
                                   onClick={() => setPreviewSequence(episode.sequence)}
                                 >
                                   <Play className="h-3.5 w-3.5 mr-1" />
                                   Preview
                                 </Button>
                               ) : (
                                 <span className="text-xs text-muted-foreground">Not ready</span>
                               )}
                             </TableCell>
                             <TableCell>
                               {uploadingFilenames[episode.sequence] && (
                                 <div className="flex items-center gap-2">
                                   <span
                                     className={`text-xs font-medium truncate max-w-[200px] ${
                                       uploadingFilenames[episode.sequence].status === 'success'
                                         ? 'text-green-600'
                                         : uploadingFilenames[episode.sequence].status === 'failed'
                                         ? 'text-red-600'
                                         : 'text-blue-600 animate-pulse'
                                     }`}
                                   >
                                     {uploadingFilenames[episode.sequence].filename}
                                   </span>
                                   <Badge
                                     variant={
                                       uploadingFilenames[episode.sequence].status === 'success'
                                         ? 'default'
                                         : uploadingFilenames[episode.sequence].status === 'failed'
                                         ? 'destructive'
                                         : 'secondary'
                                     }
                                     className="text-xs"
                                   >
                                     {uploadingFilenames[episode.sequence].status === 'success'
                                       ? '✓ Success'
                                       : uploadingFilenames[episode.sequence].status === 'failed'
                                       ? '✗ Failed'
                                       : 'Uploading...'}
                                   </Badge>
                                 </div>
                               )}
                             </TableCell>
                             <TableCell>
                               <DropdownMenu>
                                 <DropdownMenuTrigger asChild>
                                   <Button variant="ghost" size="icon" className="h-8 w-8">
                                     <MoreVertical className="h-4 w-4" />
                                   </Button>
                                 </DropdownMenuTrigger>
                                 <DropdownMenuContent align="end">
                                   <DropdownMenuItem
                                     onClick={() => {
                                       setReuploadEpisode(episode);
                                       fileInputRef.current?.click();
                                     }}
                                   >
                                     <RefreshCw className="h-4 w-4 mr-2" />
                                     Replace Episode
                                   </DropdownMenuItem>
                                   <DropdownMenuItem>
                                     <SubtitlesIcon className="h-4 w-4 mr-2" />
                                     Replace Subtitle
                                   </DropdownMenuItem>
                                   <DropdownMenuItem>
                                     <SubtitlesIcon className="h-4 w-4 mr-2" />
                                     Add Subtitle
                                   </DropdownMenuItem>
                                 </DropdownMenuContent>
                               </DropdownMenu>
                             </TableCell>
                           </TableRow>
                         ));
                         })()}
                       </TableBody>
                     </Table>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <div className="space-y-3">
                        <Video className="h-12 w-12 mx-auto opacity-50" />
                        <div>
                          <p className="text-lg font-medium">No episodes uploaded yet</p>
                          <p className="text-sm">Drag and drop video files above to get started</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* Inactive Episodes Table */}
                {contentTab === 'inactive' && (
                  <div className="rounded-lg divide-y divide-border/30">
                    {inactiveEpisodes.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow className="hover:bg-secondary/70">
                            <TableHead className="w-12 text-xs">#</TableHead>
                            <TableHead className="w-[160px] text-xs">Name</TableHead>
                            <TableHead className="w-[120px] text-xs">Original Position</TableHead>
                            <TableHead className="text-xs">Inactivated by</TableHead>
                            <TableHead className="text-xs">Inactivated At</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {inactiveEpisodes
                            .sort((a, b) => (a.originalSequence || a.sequence) - (b.originalSequence || b.sequence))
                            .map((episode) => (
                              <TableRow key={episode.id} className="hover:bg-secondary/20">
                                <TableCell className="text-xs text-muted-foreground font-mono">
                                  {String(episode.sequence).padStart(2, '0')}
                                </TableCell>
                                <TableCell className="text-sm truncate max-w-[160px]">
                                  {episode.name}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  Episode {episode.originalSequence || episode.sequence}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {episode.deletedBy || 'Unknown'}
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {episode.deletedAt
                                    ? new Date(episode.deletedAt).toLocaleString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })
                                    : 'N/A'
                                  }
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <div className="space-y-3">
                          <Trash2 className="h-12 w-12 mx-auto opacity-50" />
                          <div>
                            <p className="text-lg font-medium">No inactive episodes</p>
                            <p className="text-sm">Episodes marked as inactive will appear here</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

               </>
             )}

            {view === 'extras' && showId && (
              <BonusContentScreen 
                showId={showId} 
                userCanEdit={userCanEdit} 
              />
            )}
          </div>
        </div>
      <ShareModal open={showShareModal} onOpenChange={setShowShareModal} />
      <EpisodePreviewDialog
        open={previewSequence !== null}
        onOpenChange={(open) => {
          if (!open) setPreviewSequence(null);
        }}
        episodes={previewableEpisodes}
        initialSequence={previewSequence}
      />

      {/* Inactive Confirmation Dialog */}
      <Dialog open={showInactiveConfirm} onOpenChange={setShowInactiveConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {pendingInactiveAction?.type === 'single' && pendingInactiveAction.episodeName
                ? `Mark "${pendingInactiveAction.episodeName}" as inactive?`
                : `Mark ${pendingInactiveAction?.episodeIds.length} episode${pendingInactiveAction?.episodeIds.length && pendingInactiveAction.episodeIds.length > 1 ? 's' : ''} as inactive?`}
            </DialogTitle>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowInactiveConfirm(false);
                setPendingInactiveAction(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmInactiveAction}
            >
              Mark Inactive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading && <LoadingScreen />}
    </PublishLayout>
  );
}
